import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Badge, Button, DataTable, FormActions, FormDrawer, FormSection, IconButton, InfoBar, Modal, PageHeader, Select, Spin, TableStatusBar, useAlert } from '../ui';
import { DataTableColumn } from '../ui/DataTable';
import { useAuthHeader, useAuthUser } from 'react-auth-kit';
import { ListOrdered, Pencil, Plus, Save, Trash2 } from 'lucide-react';
import User from '../../Models/User';
import datas from "../../Assets/data.json";
import env from 'react-dotenv';
import { Crypter } from '../../Functions/Security/Crypter';

/** Modes de priorité disponibles avec leur description métier. */
const MODES_PRIORITE = [
    { value: 'FIFO', label: 'FIFO — ordre d\'arrivée strict' },
    { value: 'RDV_PARTIEL', label: 'RDV partiel — tri par heure de créneau' },
    { value: 'RDV_ABSOLU', label: 'RDV absolu — les RDV échus passent devant tout le monde' },
];

const MODE_BADGE_COLOR: Record<string, 'gray' | 'blue' | 'violet'> = {
    FIFO: 'gray',
    RDV_PARTIEL: 'blue',
    RDV_ABSOLU: 'violet',
};

/** Formate une date/heure en fr-FR, tolérant les valeurs invalides. */
function formatDateFr(v: any): string {
    if (!v) return '—'
    const d = new Date(v)
    if (isNaN(d.getTime())) return String(v)
    return d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
}

export default function GestionPriorite() {

    // USER TOKEN
    const authHeader = useAuthHeader()

    // USER SESSION
    const auth = useAuthUser()
    const user: User = JSON.parse(JSON.stringify(auth() || {}))
    const alert = useAlert()

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [dataTri, setDataTri] = useState<any[]>([])
    const [regleSelected, setRegleSelected] = useState<any>()

    // Référentiels
    const [filiales, setFiliales] = useState<any[]>([])
    const [services, setServices] = useState<any[]>([])

    // Drawer création / édition
    const [open, setOpen] = useState(false)
    const [regleEdit, setRegleEdit] = useState<any>()

    // Modale de suppression
    const [openDelete, setOpenDelete] = useState(false)
    const [regleDelete, setRegleDelete] = useState<any>()

    // Résolution du mode effectif (GET) pour une portée filiale/service
    const [resFiliale, setResFiliale] = useState('')
    const [resService, setResService] = useState('')
    const [resMode, setResMode] = useState<string | null>(null)
    const [resLoading, setResLoading] = useState(false)

    useEffect(() => {
        LoadData()
        LoadFiliales()
        LoadServices()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /** POST générique vers api/GestionFile/ManagePriorite — mutations (SET/DELETE). */
    function postPriorite(payload: any, onOk: (res: any) => void, onDone?: () => void) {
        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.GestionFile.ManagePriorite,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': authHeader(),
            },
            data: Crypter.encrypt(JSON.stringify(payload))
        };

        axios.request(config)
            .then((response: any) => {
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200) {
                    onOk(res)
                }
                else {
                    alert.warning(res.Message)
                }
            })
            .catch((error: any) => {
                alert.error('Error', error)
                console.log(error);
            })
            .finally(() => {
                if (onDone) onDone()
            });
    }

    /** GET api/GestionFile/ManagePriorite?CipherParam= — lecture seule (LIST/GET résolu). */
    function getPriorite(payload: any, onOk: (res: any) => void, onDone?: () => void) {
        let config = {
            method: 'get',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.GestionFile.ManagePrioriteGet + Crypter.encrypt(JSON.stringify(payload)),
            headers: {
                'Accept': 'text/plain',
                'Authorization': authHeader(),
            },
        };

        axios.request(config)
            .then((response: any) => {
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200 || res.StatutCode === undefined) {
                    onOk(res)
                }
                else {
                    alert.warning(res.Message)
                }
            })
            .catch((error: any) => {
                alert.error('Error', error)
                console.log(error);
            })
            .finally(() => {
                if (onDone) onDone()
            });
    }

    function LoadData() {
        setLoading(true)
        getPriorite(
            { Option: 'LIST', CodeAbonne: user.CodeAbonne },
            (res: any) => {
                const regles: any[] = (res.Result || []).map((g: any) => ({ ...g, id: g.Id }))
                setDataTri(regles)
            },
            () => setLoading(false)
        )
    }

    function LoadFiliales() {
        let config = {
            method: 'get',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.GetAllFiliales + Crypter.encrypt(user.CodeAbonne),
            headers: {
                'Accept': 'text/plain',
                'Authorization': authHeader(),
            }
        };

        axios.request(config)
            .then((response: any) => {
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200) {
                    setFiliales(res.Result || [])
                }
            })
            .catch((error: any) => {
                console.log(error);
            });
    }

    function LoadServices() {
        let config = {
            method: 'get',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.GetFilialeServices + Crypter.encrypt(user.CodeAbonne),
            headers: {
                'Accept': 'text/plain',
                'Authorization': authHeader(),
            }
        };

        axios.request(config)
            .then((response: any) => {
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200) {
                    // GetFilialeServices retourne [filiales, services] ; tolérer aussi un tableau plat
                    const svc = Array.isArray(res.Result?.[1]) ? res.Result[1] : (res.Result || [])
                    setServices(svc)
                }
            })
            .catch((error: any) => {
                console.log(error);
            });
    }

    function handleRowClick(row: any) {
        setRegleSelected(dataTri.find((r: any) => r.id == row.id))
    }

    function handleEdit(row: any) {
        setRegleEdit(dataTri.find((r: any) => r.id == row.id))
        setOpen(true)
    }

    function handleAskDelete(row: any) {
        setRegleDelete(dataTri.find((r: any) => r.id == row.id))
        setOpenDelete(true)
    }

    function handleConfirmDelete() {
        if (!regleDelete) return
        setSaving(true)
        postPriorite(
            {
                Option: 'DELETE',
                Id: regleDelete.Id,
                CodeAbonne: user.CodeAbonne,
            },
            (res: any) => {
                alert.success(res.Message)
                setOpenDelete(false)
                setRegleDelete(undefined)
                LoadData()
            },
            () => setSaving(false)
        )
    }

    function handleSaved() {
        setOpen(false)
        setRegleEdit(undefined)
        LoadData()
    }

    /** GET Option=GET — mode effectif résolu pour la portée choisie. */
    function ResolveMode() {
        const payload: any = { Option: 'GET', CodeAbonne: user.CodeAbonne }
        if (resFiliale) payload.CodeFiliale = resFiliale
        if (resService) payload.CodeS = resService
        setResLoading(true)
        getPriorite(
            payload,
            (res: any) => {
                const r = res.Result
                const mode = Array.isArray(r) ? r[0]?.ModePriorite : r?.ModePriorite
                setResMode(mode || 'FIFO')
            },
            () => setResLoading(false)
        )
    }

    const columns: DataTableColumn[] =
        [
            { field: 'Id', headerName: 'ID', width: 70 },
            {
                field: 'CodeFiliale', headerName: 'Filiale', width: 180,
                renderCell: ({ row }) => (
                    <span>{row.LibelleFiliale || row.CodeFiliale || 'Toutes'}</span>
                ),
            },
            {
                field: 'CodeS', headerName: 'Service', width: 180,
                renderCell: ({ row }) => (
                    <span>{row.LibelleService || row.CodeS || 'Tous'}</span>
                ),
            },
            {
                field: 'ModePriorite', headerName: 'Mode de priorité', width: 150,
                renderCell: ({ value }) => (
                    <Badge color={MODE_BADGE_COLOR[value] || 'gray'}>
                        {value || '—'}
                    </Badge>
                ),
            },
            {
                field: 'DateMaj', headerName: 'Dernière maj', width: 150,
                renderCell: ({ value }) => <span>{formatDateFr(value)}</span>,
            },
            {
                field: 'Actions', headerName: 'Actions', width: 110, sortable: false,
                renderCell: ({ row }) => (
                    <div className="flex items-center gap-1">
                        <IconButton tooltip="Modifier la règle" color="primary" size={30} onClick={() => handleEdit(row)}>
                            <Pencil size={15} />
                        </IconButton>
                        <IconButton tooltip="Supprimer la règle" color="danger" size={30} onClick={() => handleAskDelete(row)}>
                            <Trash2 size={15} />
                        </IconButton>
                    </div>
                ),
            },
        ];

    return (
        <Spin spinning={loading}>
            <PageHeader
                title="Priorité de la file"
                subtitle="Règles FIFO / RDV partiel / RDV absolu par filiale et service"
                actions={
                    <Button
                        size='sm'
                        variant="primary"
                        startIcon={<Plus size={16} />}
                        onClick={() => { setRegleEdit(undefined); setOpen(true); }}
                    >Nouvelle règle</Button>
                }
            />

            {/* Note explicative + résolution du mode effectif */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 mb-3">
                <div className="eb-card flex items-start gap-3 p-3">
                    <ListOrdered size={18} className="mt-0.5 flex-shrink-0 text-[var(--eb-primary)]" />
                    <p className="text-xs text-[var(--eb-text-muted)] leading-relaxed">
                        La règle la plus spécifique gagne : <strong>service + agence &gt; agence &gt; service &gt; entité</strong>.
                        FIFO = ordre d'arrivée strict, RDV partiel = tri par heure de créneau,
                        RDV absolu = les RDV échus passent devant tout le monde.
                    </p>
                </div>
                <div className="eb-card p-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-[var(--eb-text-muted)] mb-2">
                        Mode effectif — simulation par portée
                    </div>
                    <div className="flex flex-wrap items-end gap-2">
                        <div className="w-44">
                            <Select
                                label="Filiale"
                                value={resFiliale}
                                onChange={(v: string) => { setResFiliale(v); setResService(''); }}
                                options={[
                                    { value: '', label: 'Toutes' },
                                    ...filiales.map((f: any) => ({ value: f.CodeFiliale, label: f.CodeFiliale }))
                                ]}
                            />
                        </div>
                        <div className="w-44">
                            <Select
                                label="Service"
                                value={resService}
                                onChange={setResService}
                                options={[
                                    { value: '', label: 'Tous' },
                                    ...services
                                        .filter((s: any) => !resFiliale || s.CodeFiliale == resFiliale)
                                        .map((s: any) => ({ value: s.CodeS, label: s.CodeS }))
                                ]}
                            />
                        </div>
                        <Button variant='secondary' size='sm' onClick={ResolveMode} loading={resLoading}>Résoudre</Button>
                        {resMode && (
                            <Badge color={MODE_BADGE_COLOR[resMode] || 'gray'}>{resMode}</Badge>
                        )}
                    </div>
                </div>
            </div>

            <div className="eb-table-wrap">
                <DataTable
                    onRowClick={handleRowClick}
                    rows={dataTri}
                    height={300}
                    columns={columns}
                    searchable
                    emptyText="Aucune règle de priorité définie"
                />
            </div>
            <TableStatusBar count={dataTri.length} loading={loading} onRefresh={LoadData} />
            <InfoBar data={[
                { label: 'ID', value: regleSelected?.Id },
                { label: 'Filiale', value: regleSelected?.LibelleFiliale || regleSelected?.CodeFiliale || 'Toutes' },
                { label: 'Service', value: regleSelected?.LibelleService || regleSelected?.CodeS || 'Tous' },
                { label: 'Mode', value: regleSelected?.ModePriorite },
                { label: 'Dernière maj', value: formatDateFr(regleSelected?.DateMaj) },
            ]} />

            <FormDrawer
                open={open}
                onClose={() => { setOpen(false); setRegleEdit(undefined); }}
                title={regleEdit ? `Modification de la règle ${regleEdit?.Id ?? ''}` : "Nouvelle règle de priorité"}
                hint="Définissez le périmètre (filiale / service) et le mode de priorité. Laissez vide pour une règle globale."
            >
                <FormPriorite
                    regle={regleEdit}
                    filiales={filiales}
                    services={services}
                    onSaved={handleSaved}
                />
            </FormDrawer>

            <Modal
                open={openDelete}
                onClose={() => { setOpenDelete(false); setRegleDelete(undefined); }}
                title="Supprimer la règle de priorité"
                footer={
                    <>
                        <Button variant="outline" onClick={() => { setOpenDelete(false); setRegleDelete(undefined); }}>Annuler</Button>
                        <Button variant="danger" loading={saving} startIcon={<Trash2 size={16} />} onClick={handleConfirmDelete}>Supprimer</Button>
                    </>
                }
            >
                <p className="text-sm text-[var(--eb-text)]">
                    Confirmez-vous la suppression de la règle
                    {regleDelete ? ` (ID ${regleDelete.Id} — ${regleDelete.ModePriorite || ''})` : ''} ?
                    Cette action est irréversible.
                </p>
            </Modal>
        </Spin>
    );
}

/* ------------------------------------------------------------------ */
/*  Formulaire de création / édition d'une règle de priorité          */
/* ------------------------------------------------------------------ */
function FormPriorite(props: { regle?: any; filiales: any[]; services: any[]; onSaved: () => void }) {

    // USER TOKEN
    const authHeader = useAuthHeader()

    // USER SESSION
    const auth = useAuthUser()
    const user: User = JSON.parse(JSON.stringify(auth() || {}))
    const alert = useAlert()

    const [loading, setLoading] = useState(false)
    const [modePriorite, setModePriorite] = useState<string>(props.regle?.ModePriorite || 'FIFO')
    const [codeFiliale, setCodeFiliale] = useState<string>(props.regle?.CodeFiliale || '')
    const [codeS, setCodeS] = useState<string>(props.regle?.CodeS || '')

    // Services filtrés par la filiale choisie (si une filiale est sélectionnée)
    const servicesFiltres = props.services.filter((s: any) => !codeFiliale || s.CodeFiliale == codeFiliale)

    const handleSubmit = () => {
        if (!modePriorite) {
            alert.warning("Veuillez sélectionner un mode de priorité.")
            return
        }
        setLoading(true)
        // SET : crée ou met à jour la règle pour la portée (CodeFiliale/CodeS optionnels)
        const payload: any = {
            Option: 'SET',
            CodeAbonne: user.CodeAbonne,
            ModePriorite: modePriorite
        }
        if (codeFiliale) payload.CodeFiliale = codeFiliale
        if (codeS) payload.CodeS = codeS

        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.GestionFile.ManagePriorite,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': authHeader(),
            },
            data: Crypter.encrypt(JSON.stringify(payload))
        };

        axios.request(config)
            .then((response: any) => {
                setLoading(false)
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200) {
                    alert.success(res.Message)
                    props.onSaved()
                }
                else {
                    alert.warning(res.Message)
                }
            })
            .catch((error: any) => {
                setLoading(false)
                alert.error('Error', error)
                console.log(error);
            });
    }

    return (
        <Spin spinning={loading}>
            <FormSection title="Mode de priorité" subtitle="Comportement de la file d'attente">
                <Select
                    label="Mode de priorité"
                    value={modePriorite}
                    onChange={setModePriorite}
                    options={MODES_PRIORITE}
                />
            </FormSection>

            <FormSection title="Périmètre" subtitle="Filiale et service concernés — laisser vide pour une règle globale">
                <Select
                    label="Filiale"
                    value={codeFiliale}
                    onChange={(v: string) => { setCodeFiliale(v); setCodeS(''); }}
                    options={[
                        { value: '', label: 'Toutes les filiales' },
                        ...props.filiales.map((f: any) => ({ value: f.CodeFiliale, label: `${f.CodeFiliale} - ${f.Libelle || ''}` }))
                    ]}
                />
                <Select
                    label="Service"
                    value={codeS}
                    onChange={setCodeS}
                    options={[
                        { value: '', label: 'Tous les services' },
                        ...servicesFiltres.map((s: any) => ({ value: s.CodeS, label: `${s.CodeS} - ${s.Libelle || ''}` }))
                    ]}
                />
            </FormSection>

            <FormActions>
                <Button variant='secondary' type='button' loading={loading} startIcon={<Save size={16} />} onClick={handleSubmit}>
                    Enregistrer
                </Button>
            </FormActions>
        </Spin>
    );
}
